import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useCare } from '../contexts/CareContext'
import LoadingSpinner from '../components/shared/LoadingSpinner'
import { matchRiskFlags } from '../lib/riskFlags'
import { isFreePlan } from '../lib/plan'
import { parseGoals } from '../lib/carePlan'
import { buildDomains, TONE_COLOR } from '../lib/functional'
import RequestCallModal from '../components/shared/RequestCallModal'

const todayISO = () => new Date().toLocaleDateString('en-CA')
const fmtDate = (iso) =>
  iso ? new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''

function moodEmoji(avg) {
  if (avg == null) return '—'
  if (avg >= 4.5) return '😊'
  if (avg >= 3.5) return '🙂'
  if (avg >= 2.5) return '😐'
  if (avg >= 1.5) return '😕'
  return '😟'
}

const PRIO_LABEL = { high: 'High priority', medium: 'Medium priority', ongoing: 'Ongoing' }
const PRIO_RANK = { high: 0, medium: 1, ongoing: 2 }

function Tile({ icon, label, value, tone }) {
  const color = tone === 'good' ? 'var(--green)' : tone === 'warn' ? 'var(--amber)' : 'var(--text)'
  return (
    <div className="card" style={{ padding: 12, textAlign: 'center' }}>
      <div style={{ fontSize: 20, lineHeight: 1.2 }} aria-hidden>{icon}</div>
      <div style={{ fontWeight: 700, fontSize: 15, marginTop: 4, color }}>{value}</div>
      <div className="muted" style={{ fontSize: 11, marginTop: 1 }}>{label}</div>
    </div>
  )
}

export default function DashboardPage() {
  const { recipient } = useCare()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [d, setD] = useState(null)
  const [callModal, setCallModal] = useState(null)

  const today = todayISO()

  const load = useCallback(async () => {
    if (!recipient) return
    setLoading(true)
    const rid = recipient.id

    const [logsToday, anyLog, medRes, medLogRes, taskRes, apptRes, incRes, planRes, assessRes, actionRes] =
      await Promise.all([
        supabase.from('care_logs').select('*').eq('care_recipient_id', rid).eq('log_date', today),
        supabase.from('care_logs').select('id', { count: 'exact', head: true }).eq('care_recipient_id', rid),
        supabase.from('medications').select('id', { count: 'exact', head: true })
          .eq('care_recipient_id', rid).eq('is_active', true),
        supabase.from('medication_logs').select('medication_id')
          .eq('care_recipient_id', rid).eq('log_date', today),
        supabase.from('tasks').select('*, assignee:profiles!assigned_to(full_name)')
          .eq('care_recipient_id', rid).eq('is_completed', false)
          .order('due_date', { ascending: true, nullsFirst: false }).limit(5),
        supabase.from('appointments')
          .select('id, doctor_name, specialty, purpose, appointment_date, appointment_time')
          .eq('care_recipient_id', rid).gte('appointment_date', today)
          .order('appointment_date', { ascending: true }).limit(1),
        supabase.from('incidents').select('id', { count: 'exact', head: true })
          .eq('care_recipient_id', rid).eq('incident_date', today),
        supabase.from('care_plans').select('*').eq('care_recipient_id', rid).eq('is_active', true)
          .order('created_at', { ascending: false }).limit(1).maybeSingle(),
        supabase.from('functional_assessments').select('*').eq('care_recipient_id', rid)
          .order('assessment_date', { ascending: false }).limit(1).maybeSingle(),
        // Open care-plan actions. Resolves to an error object (not a throw)
        // until the care_plan_actions migration is run, so the count falls
        // back to 0 and the extra line simply doesn't render.
        supabase.from('care_plan_actions').select('id', { count: 'exact', head: true })
          .eq('care_recipient_id', rid).eq('is_completed', false),
      ])

    const logs = logsToday.data || []
    const moods = []
    for (const l of logs) {
      for (const m of [l.mood_morning, l.mood_afternoon, l.mood_evening]) if (m != null) moods.push(m)
    }
    const moodAvg = moods.length
      ? Math.round((moods.reduce((a, b) => a + b, 0) / moods.length) * 10) / 10
      : null

    setD({
      logs,
      hasCareLogs: (anyLog.count || 0) > 0,
      moodAvg,
      medsActive: medRes.count || 0,
      medsLogged: new Set((medLogRes.data || []).map((r) => r.medication_id)).size,
      tasks: taskRes.data || [],
      appt: (apptRes.data || [])[0] || null,
      incidents: incRes.count || 0,
      walkDone: logs.some((l) => l.task_mobility === true),
      plan: planRes.data || null,
      assessment: assessRes.data || null,
      openActions: actionRes.count || 0,
    })
    setLoading(false)
  }, [recipient, today])

  useEffect(() => { load() }, [load])

  if (loading || !d) return <LoadingSpinner label="Loading today…" />

  const name = recipient?.preferred_name || recipient?.full_name || 'them'
  const goals = parseGoals(d.plan?.current_priorities)
  const topGoal = [...goals].sort(
    (a, b) => (PRIO_RANK[a.priority] ?? 3) - (PRIO_RANK[b.priority] ?? 3)
  )[0]
  const hasAssessment = !!d.assessment
  const firstTime = !d.hasCareLogs
  const flags = matchRiskFlags(recipient?.conditions).slice(0, 2)

  /* ── Cards ─────────────────────────────────────────────── */

  const CarePlanCard = ({ compact }) => {
    if (!d.plan) {
      return (
        <div className="card" style={{ borderColor: 'var(--pa)', borderWidth: 1.5 }}>
          <div style={{ fontWeight: 700 }}>No care plan yet</div>
          <div className="muted" style={{ fontSize: 14, marginTop: 4, lineHeight: 1.5 }}>
            Ginie can build one with goals, risk flags, and daily actions.
          </div>
          <button
            type="button"
            className="btn btn-sm mt-16"
            onClick={() => setCallModal('unsure')}
          >
            Generate care plan →
          </button>
        </div>
      )
    }
    return (
      <div className="card" style={{ borderColor: 'var(--pa)', borderWidth: 1.5 }}>
        <div style={{ fontWeight: 700, color: 'var(--pa)' }}>
          ✓ Care plan ready for {name}
        </div>
        {topGoal && (
          <div className="row" style={{ gap: 8, marginTop: compact ? 6 : 10, alignItems: 'flex-start' }}>
            <span className={`dot dot-${topGoal.priority}`} style={{ marginTop: 6 }} aria-hidden />
            <div>
              <div style={{ fontWeight: 600, fontSize: 15, lineHeight: 1.4 }}>{topGoal.title}</div>
              <div className={`prio-label prio-${topGoal.priority}`} style={{ marginTop: 2 }}>
                {PRIO_LABEL[topGoal.priority]}
              </div>
            </div>
          </div>
        )}
        <button
          onClick={() => navigate('/goals')}
          style={{ background: 'none', border: 'none', padding: 0, marginTop: 12, color: 'var(--pa)', fontWeight: 600, fontSize: 14 }}
        >
          See all {goals.length || ''} goal{goals.length === 1 ? '' : 's'} →
        </button>
        {d.openActions > 0 && (
          <button
            onClick={() => navigate('/care-plan-actions')}
            style={{ display: 'block', background: 'none', border: 'none', padding: 0, marginTop: 6, color: 'var(--pa)', fontWeight: 600, fontSize: 14, textAlign: 'left' }}
          >
            {d.openActions} action{d.openActions === 1 ? '' : 's'} in progress →
          </button>
        )}
        {!compact && (
          <div className="muted" style={{ fontSize: 13, marginTop: 6 }}>
            Review and update goals monthly.
          </div>
        )}
      </div>
    )
  }

  const TasksCard = ({ onlyTodayAndOverdue }) => {
    const list = onlyTodayAndOverdue
      ? d.tasks.filter((t) => t.due_date && t.due_date <= today).slice(0, 3)
      : d.tasks.slice(0, 3)
    return (
      <div>
        <div className="section-title" style={{ marginBottom: 8 }}>Tasks for {name}</div>
        {d.tasks.length === 0 ? (
          <div className="card">
            <div style={{ fontWeight: 600 }}>No tasks yet</div>
            <div className="muted" style={{ fontSize: 14, marginTop: 2, lineHeight: 1.5 }}>
              Your care plan suggested some.
            </div>
            <button className="btn btn-sm mt-16" onClick={() => navigate('/tasks')}>
              + Add first task
            </button>
          </div>
        ) : list.length === 0 ? (
          <div className="card muted" style={{ fontSize: 14 }}>Nothing due today.</div>
        ) : (
          <div className="stack" style={{ gap: 8 }}>
            {list.map((t) => {
              const overdue = t.due_date && t.due_date < today
              return (
                <button key={t.id} className="card card-tap" onClick={() => navigate('/tasks')}>
                  <div style={{ fontWeight: 600 }}>{t.title}</div>
                  <div className="row wrap" style={{ gap: 6, marginTop: 6 }}>
                    {t.assignee?.full_name ? (
                      <span className="badge">{t.assignee.full_name}</span>
                    ) : (
                      <span className="badge badge-amber">Unassigned — tap to assign</span>
                    )}
                    {t.due_date && (
                      <span className={`badge${overdue ? ' badge-danger' : ''}`}>
                        {overdue ? 'Overdue ' : 'Due '}{fmtDate(t.due_date)}
                      </span>
                    )}
                  </div>
                </button>
              )
            })}
          </div>
        )}
        {d.tasks.length > 0 && (
          <button
            onClick={() => navigate('/tasks')}
            style={{ background: 'none', border: 'none', padding: 0, marginTop: 10, color: 'var(--pa)', fontWeight: 600, fontSize: 14 }}
          >
            See all tasks and assign →
          </button>
        )}
      </div>
    )
  }

  const AssessmentCard = () => {
    if (hasAssessment) {
      const domains = buildDomains(recipient, d.assessment).slice(0, 3)
      const last = d.assessment.assessment_date
      const next = (() => {
        const b = new Date(last + 'T00:00:00'); b.setMonth(b.getMonth() + 1)
        return b.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
      })()
      return (
        <button className="card card-tap" onClick={() => navigate('/tracking?tab=function')}>
          <div className="section-title">Functional assessment</div>
          <div className="row wrap" style={{ gap: 14, marginTop: 10 }}>
            {domains.map((x) => (
              <div key={x.key} className="row" style={{ gap: 6 }}>
                <span
                  aria-hidden
                  style={{
                    width: 10, height: 10, borderRadius: 999,
                    background: TONE_COLOR[x.tone] || 'var(--muted)',
                  }}
                />
                <span style={{ fontSize: 13 }}>{x.name}</span>
              </div>
            ))}
          </div>
          <div className="muted" style={{ fontSize: 13, marginTop: 10 }}>
            Last done {fmtDate(last)} · Next due {next}
          </div>
        </button>
      )
    }
    return (
      <div className="card">
        <div style={{ fontWeight: 700 }}>Functional assessment</div>
        <div className="muted" style={{ fontSize: 14, marginTop: 4, lineHeight: 1.55 }}>
          Measure how {name} is doing across 6 domains — mobility, memory, hearing,
          vision, strength, mood.
        </div>
        {/* Stacked, not side by side: at 390px a half-width button is ~143px
            of text space and "Book a nurse assessment" needs ~165px. */}
        <div className="stack" style={{ gap: 8, marginTop: 14 }}>
          <button className="btn btn-sm btn-block" onClick={() => navigate('/tracking?tab=function')}>
            Do it yourself →
          </button>
          <button className="btn btn-sm btn-secondary btn-block" onClick={() => navigate('/more')}>
            Book a nurse assessment
          </button>
        </div>
      </div>
    )
  }

  const StartTrackingCard = () => (
    <div className="card">
      <div style={{ fontWeight: 700 }}>Start tracking {name}'s day</div>
      <div className="muted" style={{ fontSize: 14, marginTop: 4, lineHeight: 1.55 }}>
        Log mood, medications, vitals, and incidents daily. The more you track,
        the clearer the picture.
      </div>
      <div className="row wrap" style={{ gap: 8, marginTop: 14 }}>
        <button className="chip" onClick={() => navigate('/?add=care-log')}>📝 Log care note</button>
        <button className="chip" onClick={() => navigate('/medications?tab=today')}>💊 Log medications</button>
        <button className="chip" onClick={() => navigate('/?add=vitals')}>🩺 Log vitals</button>
      </div>
    </div>
  )

  const UpgradeCard = () => (
    <div className="card" style={{ borderColor: 'var(--pa)', borderWidth: 1.5 }}>
      <div style={{ fontWeight: 700 }}>Want someone calling {name} every day?</div>
      <div className="muted" style={{ fontSize: 14, marginTop: 4, lineHeight: 1.55 }}>
        A MaPa coordinator calls for 5 minutes every morning — logs everything,
        flags concerns, keeps your whole family informed.
      </div>
      <button className="btn btn-block mt-16" onClick={() => setCallModal('connect')}>
        Get daily check-ins — $197/month →
      </button>
    </div>
  )

  const GlanceCard = () => (
    <div>
      <div className="section-title" style={{ marginBottom: 8 }}>Today at a glance</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
        <Tile icon={moodEmoji(d.moodAvg)} label="Mood" value={d.moodAvg ?? '—'} />
        <Tile
          icon="💊" label="Meds"
          value={d.medsActive ? `${d.medsLogged}/${d.medsActive}` : '—'}
          tone={d.medsActive && d.medsLogged >= d.medsActive ? 'good' : d.medsActive ? 'warn' : undefined}
        />
        <Tile icon="🚶" label="Movement" value={d.walkDone ? '✓' : '—'} tone={d.walkDone ? 'good' : undefined} />
        <Tile icon="⚠️" label="Incidents" value={d.incidents} tone={d.incidents > 0 ? 'warn' : 'good'} />
      </div>
    </div>
  )

  const FunctionalCompact = () => {
    const domains = buildDomains(recipient, d.assessment).slice(0, 4)
    return (
      <button className="card card-tap" onClick={() => navigate('/tracking?tab=function')}>
        <div className="section-title">Functional status</div>
        <div className="row wrap" style={{ gap: 12, marginTop: 8 }}>
          {domains.map((x) => (
            <div key={x.key} className="row" style={{ gap: 6 }}>
              <span
                aria-hidden
                style={{ width: 10, height: 10, borderRadius: 999, background: TONE_COLOR[x.tone] || 'var(--muted)' }}
              />
              <span style={{ fontSize: 13 }}>{x.name}</span>
            </div>
          ))}
        </div>
      </button>
    )
  }

  const AppointmentCard = () =>
    d.appt ? (
      <button className="card card-tap" onClick={() => navigate('/appointments')}>
        <div className="spread">
          <div>
            <div className="section-title">Next appointment</div>
            <div style={{ fontWeight: 700, marginTop: 4 }}>{d.appt.doctor_name}</div>
            <div className="muted" style={{ fontSize: 14 }}>
              {d.appt.specialty || d.appt.purpose || ''}
            </div>
          </div>
          <div className="center">
            <div style={{ fontWeight: 700, fontSize: 14 }}>{fmtDate(d.appt.appointment_date)}</div>
            {d.appt.appointment_time && (
              <div className="muted" style={{ fontSize: 12 }}>{d.appt.appointment_time.slice(0, 5)}</div>
            )}
          </div>
        </div>
      </button>
    ) : null

  const RiskFlags = () =>
    flags.length > 0 ? (
      <div className="stack" style={{ gap: 8 }}>
        {flags.map((f) => (
          <div key={f.key} className={`card ${f.severity === 'high' ? 'tint-high' : 'tint-medium'}`}>
            <div className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
              <span style={{ fontSize: 18, lineHeight: 1.2 }} aria-hidden>⚠️</span>
              <div>
                <div style={{ fontWeight: 700, color: f.severity === 'high' ? 'var(--danger)' : '#8A5C07' }}>
                  {f.label}
                </div>
                <div style={{ fontSize: 13, marginTop: 2, lineHeight: 1.5 }}>{f.reason}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    ) : null

  /* ── Layouts ───────────────────────────────────────────── */

  if (firstTime) {
    return (
      <div className="stack">
        <CarePlanCard />
        <RiskFlags />
        <TasksCard />
        <AssessmentCard />
        <StartTrackingCard />
        {isFreePlan(recipient) && <UpgradeCard />}
        <AppointmentCard />
        <div style={{ paddingBottom: 80 }} />
        {callModal && (
          <RequestCallModal plan={callModal} onClose={() => setCallModal(null)} />
        )}
      </div>
    )
  }

  return (
    <div className="stack">
      {d.logs.length > 0 && <GlanceCard />}
      <RiskFlags />
      <CarePlanCard compact />
      <TasksCard onlyTodayAndOverdue />
      <FunctionalCompact />
      <AppointmentCard />
      {isFreePlan(recipient) && <UpgradeCard />}
      <div style={{ height: 8 }} />
      {callModal && (
        <RequestCallModal plan={callModal} onClose={() => setCallModal(null)} />
      )}
    </div>
  )
}
